import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Home } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { usePWA } from "@/hooks/usePWA";
import { useToast } from "@/hooks/use-toast";
import { getPlatform, getPlatformInstructions } from "@/utils/pwaDetection";
import { ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { trackPWAEvent } from "@/utils/pwaAnalytics";

type InstallModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isOnline: boolean;
};

export const InstallModal = ({ open, onOpenChange, isOnline }: InstallModalProps) => {
  const { canInstall, isInstalled, updateAvailable, promptInstall } = usePWA();
  const { toast } = useToast();
  const [troubleshootingOpen, setTroubleshootingOpen] = useState(false);
  const platform = getPlatform();
  const instructions = getPlatformInstructions(platform);

  // Track modal opened
  useEffect(() => {
    if (open) {
      trackPWAEvent('modal_opened', 'badge', canInstall, isInstalled);
    }
  }, [open, canInstall, isInstalled]);

  // Determine status and configuration
  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        icon: '📴',
        title: 'You\'re Offline',
        description: 'Connect to the internet to install Wormble as an app',
        bgColor: 'bg-muted',
        borderColor: 'border-muted-foreground/20',
      };
    }

    if (updateAvailable) {
      return {
        icon: '🔄',
        title: 'Update Available',
        description: 'A new version of Wormble is ready - refresh to update',
        bgColor: 'bg-blue-50 dark:bg-blue-950/20',
        borderColor: 'border-blue-200 dark:border-blue-800',
      };
    }

    if (isInstalled) {
      return {
        icon: '✅',
        title: 'Wormble is Already Installed',
        description: 'Open from your home screen for the full app experience',
        bgColor: 'bg-green-50 dark:bg-green-950/20',
        borderColor: 'border-green-200 dark:border-green-800',
        note: 'If you\'re seeing this in your browser, close this tab and tap the Wormble icon on your home screen instead.',
      };
    }

    if (canInstall) {
      return {
        icon: '🎯',
        title: 'Ready to Install',
        description: 'Follow the steps below to install Wormble as an app',
        bgColor: 'bg-primary/5',
        borderColor: 'border-primary/20',
      };
    }

    return {
      icon: '💻',
      title: 'Browser Mode',
      description: 'Installation not available, but you can still play in your browser',
      bgColor: 'bg-muted',
      borderColor: 'border-muted-foreground/20',
    };
  };

  const statusConfig = getStatusConfig();

  const handleInstallNow = () => {
    trackPWAEvent('install_clicked', 'badge', canInstall, isInstalled);
    
    if (promptInstall) {
      toast({
        title: "Installing Wormble...",
        description: "Follow the browser prompt to add to your device.",
      });
      promptInstall();
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    trackPWAEvent('modal_dismissed', 'badge', canInstall, isInstalled);
    onOpenChange(false);
  };

  const handleUpdate = () => {
    toast({
      title: "Updating Wormble...",
      description: "Refreshing to get the latest version.",
    });
    setTimeout(() => window.location.reload(), 500);
  };

  const handleOpenInSafari = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      toast({
        title: "URL Copied!",
        description: "Paste this URL in Safari to install Wormble.",
      });
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[380px] p-6" aria-describedby="install-modal-description">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center mb-2">
            {instructions.title}
          </DialogTitle>
          <p id="install-modal-description" className="sr-only">
            Instructions for installing Wormble as a Progressive Web App on your device
          </p>
          {instructions.timeEstimate && (
            <div className="flex justify-center">
              <Badge variant="secondary" className="text-xs">
                {instructions.timeEstimate}
              </Badge>
            </div>
          )}
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-4 mt-4 pr-4">
            {/* Intro Section */}
            {instructions.intro && (
              <p className="text-sm text-muted-foreground text-center">
                {instructions.intro}
              </p>
            )}

            {/* Status Section */}
            <div className={`p-4 rounded-lg border ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">{statusConfig.icon}</span>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{statusConfig.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {statusConfig.description}
                  </p>
                </div>
              </div>
              {isInstalled && statusConfig.note && (
                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                  💡 {statusConfig.note}
                </p>
              )}
            </div>

            {/* Warning Message */}
            {instructions.warning && (
              <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  ⚠️ {instructions.warning}
                </p>
              </div>
            )}

            {/* Installation Steps */}
            {instructions.steps.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-sm">How to Install</h3>
                <div className="space-y-4">
                  {instructions.steps.map((step, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{step.icon}</span>
                          <p className="font-medium text-sm">{step.title}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {step.description}
                        </p>
                        {step.locationHint && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <span>📍</span>
                            {step.locationHint}
                          </p>
                        )}
                        {step.tip && (
                          <p className="text-xs text-primary/80 flex items-center gap-1 mt-1">
                            <span>💡</span>
                            {step.tip}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* After Installing Section */}
            {instructions.afterInstalling && instructions.afterInstalling.length > 0 && (
              <div className="space-y-2 p-3 rounded-lg bg-muted/50">
                <h3 className="font-semibold text-sm">After Installing</h3>
                <ul className="space-y-1.5">
                  {instructions.afterInstalling.map((item, index) => (
                    <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            {canInstall && !isInstalled && (
              <Button onClick={handleInstallNow} className="w-full">
                Install Now
              </Button>
            )}

            {updateAvailable && (
              <Button onClick={handleUpdate} className="w-full">
                Update Available - Refresh Now
              </Button>
            )}

            {isInstalled && (
              <Button 
                variant="outline" 
                onClick={handleClose} 
                className="w-full gap-2"
              >
                <Home className="h-4 w-4" />
                Back to Game
              </Button>
            )}

            {instructions.showOpenInSafari && (
              <Button onClick={handleOpenInSafari} className="w-full">
                Open in Safari
              </Button>
            )}

            {/* Benefits Section */}
            <div className="space-y-3 pt-2 border-t">
              <h3 className="font-semibold text-sm">Why Install Wormble?</h3>
              <div className="space-y-2">
                {[
                  { icon: '⚡', text: 'Launches in under 1 second — faster than most apps' },
                  { icon: '💾', text: 'Uses only ~2MB of storage (vs 50-200MB for typical games)' },
                  { icon: '📴', text: 'Works fully offline — play on planes, subways, anywhere' },
                  { icon: '🔄', text: 'Always up-to-date — no manual updates needed' },
                  { icon: '🔥', text: 'Never lose your streak — progress syncs automatically' },
                  { icon: '📱', text: 'Full-screen experience without browser clutter' },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">{benefit.icon}</span>
                    <span className="text-xs text-muted-foreground pt-1">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Troubleshooting Section */}
            {instructions.troubleshooting && instructions.troubleshooting.length > 0 && (
              <Collapsible open={troubleshootingOpen} onOpenChange={setTroubleshootingOpen}>
                <CollapsibleTrigger asChild>
                  <button 
                    className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    aria-label="Toggle troubleshooting tips"
                  >
                    <span className="font-semibold text-sm">Having trouble?</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${troubleshootingOpen ? 'rotate-180' : ''}`} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="space-y-2 p-3 rounded-lg bg-muted/30">
                    <ul className="space-y-2">
                      {instructions.troubleshooting.map((tip, index) => (
                        <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Note */}
            {instructions.note && (
              <p className="text-xs text-center text-muted-foreground pt-2 border-t">
                {instructions.note}
              </p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
